import Link from './link';
import className from './styles.module.css';
import logo from '../../assets/images/white-logo.svg';
import whiteCloseLogo from '../../assets/images/white-close-logo.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleUser,
  faCalendar,
  faListCheck,
  faClock,
  faBuilding,
  faTruckFront,
  faUserGroup,
  faArrowLeft,
  faArrowRight,
  faAngleDown,
  faAngleUp,
  faRoute,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { Suspense, lazy, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCurrentUserQuery } from '../../api/userApi';
import { AppDispatch, AppState, resetStateOnLogout } from '../../redux/store';
import {
  closeAccountMenuPopup,
  openAccountMenu,
  stopSidebarOverlaying,
  toggleAccountMenuOpen,
  toggleAccountMenuPopup,
  toggleSidebarCollapsing,
} from '../../redux/settingSlice';
import PolicyRequired from '../common/PolicyRequired';
import { useGetCalcAllocationStatusWithoutLoadingQuery } from '../../api/carAllocationApi';
import { useLocation } from 'react-router-dom';
import PcOnly from '../common/PcOnly';
import { isFetchBaseQueryError } from '../../services/isFetchBaseQueryError';
import { isApiErrorDataObject } from '../../services/isApiErrorDataObject';

const AccountPopup = lazy(() => import('./AccountPopup'));

function SideBar() {
  const { data: currentUser } = useGetCurrentUserQuery();
  const collapsed = useSelector(
    (state: AppState) => state.settings.sidebar.collapsed
  );
  const openSideber = !collapsed;
  const accountMenuOpen = useSelector(
    (state: AppState) => state.settings.accountMenu.open
  );
  const accountMenuPopup = useSelector(
    (state: AppState) => state.settings.accountMenu.popup
  );

  const status = useCalcAllocationStatus();

  const allocations = (
    <PolicyRequired policy={33}>
      <PcOnly>
        <Link
          to="/allocations"
          isClose={openSideber}
          icon={<FontAwesomeIcon icon={faCalendar} />}
          title={'配車表'}
        />
      </PcOnly>
    </PolicyRequired>
  );

  const contracts = (
    <PolicyRequired policy={18}>
      <PcOnly>
        <Link
          to="/contracts"
          isClose={openSideber}
          icon={<FontAwesomeIcon icon={faListCheck} />}
          title={'受注入力/配車計算'}
          badge={status?.isConditionUpdated}
        />
      </PcOnly>
    </PolicyRequired>
  );

  const calendar = (
    <PolicyRequired policy={8}>
      <PcOnly>
        <Link
          to="/calendar"
          isClose={openSideber}
          icon={<FontAwesomeIcon icon={faClock} />}
          title={'勤務表'}
        />
      </PcOnly>
    </PolicyRequired>
  );

  const companies = (
    <PolicyRequired policy={23}>
      <PcOnly>
        <Link
          to="/companies"
          isClose={openSideber}
          icon={<FontAwesomeIcon icon={faBuilding} />}
          title={'会社情報'}
        />
      </PcOnly>
    </PolicyRequired>
  );

  const cars = (
    <PolicyRequired policy={12}>
      <PcOnly>
        <Link
          to="/cars"
          isClose={openSideber}
          icon={<FontAwesomeIcon icon={faTruckFront} />}
          title={'車両情報'}
        />
      </PcOnly>
    </PolicyRequired>
  );

  const drivers = (
    <PolicyRequired policy={3}>
      <PcOnly>
        <Link
          to="/drivers"
          isClose={openSideber}
          icon={<FontAwesomeIcon icon={faUserGroup} />}
          title={'ドライバー情報'}
        />
      </PcOnly>
    </PolicyRequired>
  );

  const operatingRoute = (
    <PolicyRequired policy={35}>
      <Link
        to="/operating-route"
        isClose={openSideber}
        icon={<FontAwesomeIcon icon={faRoute} />}
        title={'運行ルート'}
      />
    </PolicyRequired>
  );

  const dispatch = useDispatch<AppDispatch>();

  const [popupReference, setPopupReference] = useState<HTMLDivElement | null>(
    null
  );

  useAccountMenuInitiallyOpen();

  return (
    <div
      className={classNames(
        className.container,
        collapsed && className.collapsed
      )}
    >
      <div
        className={className.backdrop}
        onClick={() => dispatch(stopSidebarOverlaying())}
      />
      <div className={className.sidebar}>
        <div className={className.logoBackground}>
          {openSideber ? (
            <img width={134} height={38} src={logo} />
          ) : (
            <img width={34} height={38} src={whiteCloseLogo} />
          )}
        </div>
        {
          // 中途半端な UI を表示しないように API の読み込みが完了するまで全体を非表示にしておく
          currentUser && (
            <>
              {!openSideber && accountMenuPopup && popupReference && (
                <Suspense>
                  <AccountPopup
                    reference={popupReference}
                    onRequestClose={() => dispatch(closeAccountMenuPopup())}
                  />
                </Suspense>
              )}
              <div
                className={className.userNameBackground}
                onClick={() =>
                  dispatch(
                    openSideber
                      ? toggleAccountMenuOpen()
                      : toggleAccountMenuPopup()
                  )
                }
                ref={setPopupReference}
              >
                <div className={className.icon}>
                  <FontAwesomeIcon icon={faCircleUser} />
                </div>
                {openSideber && (
                  <>
                    <div className={className.userName}>
                      {currentUser.userName}
                    </div>
                    <button type="button" className={className.iconButton}>
                      <FontAwesomeIcon
                        icon={accountMenuOpen ? faAngleUp : faAngleDown}
                      />
                    </button>
                  </>
                )}
              </div>
              {openSideber && accountMenuOpen && (
                <ul className={className.userList}>
                  <PolicyRequired policy={2}>
                    <Link
                      to="/mypage"
                      isClose={openSideber}
                      title="マイページ"
                    />
                  </PolicyRequired>
                  <Link
                    to="/login"
                    isClose={openSideber}
                    onClick={() => {
                      resetStateOnLogout();
                    }}
                    title="ログアウト"
                  />
                </ul>
              )}
              <ul className={className.list}>
                {operatingRoute}
                {allocations}
                {contracts}
                {calendar}
              </ul>
              <ul className={className.list}>
                {companies}
                {cars}
                {drivers}
              </ul>
            </>
          )
        }
        <PcOnly>
          <div
            className={className.close}
            onClick={() => {
              dispatch(toggleSidebarCollapsing());
            }}
          >
            <button type="button" className={className.iconButton}>
              <FontAwesomeIcon
                icon={openSideber ? faArrowLeft : faArrowRight}
              />
            </button>
            {openSideber && (
              <div className={className.closeFont}>メニューを閉じる</div>
            )}
          </div>
        </PcOnly>
      </div>
      <CloseButtonOnShadow onClick={() => dispatch(stopSidebarOverlaying())} />
    </div>
  );
}

export default SideBar;

function CloseButtonOnShadow({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      className={className.closeButtonOnShadow}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={faXmark} />
    </button>
  );
}

let accountMenuHasOpened = false;
/**
 * (トップレベルナビゲーションで) ページ表示したときに特定画面ならアカウントメニューを開くようにする
 */
function useAccountMenuInitiallyOpen() {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (accountMenuHasOpened) return;
    if (!location.pathname.match(/^\/mypage\/?/)) return;
    dispatch(openAccountMenu());
    accountMenuHasOpened = true;
  }, []);
}

/**
 * 配車計算ステータス (ポーリングしてデータを取得する)
 */
function useCalcAllocationStatus() {
  const location = useLocation();
  const [hasError, setHasError] = useState(false);

  const { data: status, error } = useGetCalcAllocationStatusWithoutLoadingQuery(
    undefined,
    {
      // 30秒おきにポーリング
      pollingInterval: 30000,
      // エラー発生後はポーリングを止める
      skip: hasError,
    }
  );

  // エラー発生を記録する
  useEffect(() => {
    if (!error) {
      return;
    }

    const isAuthenticationError =
      isFetchBaseQueryError(error) &&
      isApiErrorDataObject(error.data) &&
      error.data.code === 401;
    // 401エラーは認証エラーのためポーリングを止めない
    if (isAuthenticationError) {
      return;
    }

    setHasError(true);
  }, [error]);

  // 画面遷移時に再取得する
  useEffect(() => {
    setHasError(false);
  }, [location.pathname]);

  return status;
}
